import { createTheme } from '@mui/material/styles';

// Enhanced cyberpunk color palette inspired by the anime trading theme
const neonColors = {
  cyan: '#00ffff',
  magenta: '#ff00ff',
  purple: '#9945ff',
  pink: '#ff0066',
  green: '#00ff88',
  lime: '#88ff00',
  orange: '#ff9500',
  blue: '#0099ff',
  violet: '#7c4dff',
  gold: '#ffd700',
};

export const arbitreasureTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: neonColors.cyan, // Treasure Cyan
      light: '#66ffff',
      dark: '#00cccc',
    },
    secondary: {
      main: neonColors.magenta, // Enhanced Magenta
      light: '#ff66ff',
      dark: '#cc00cc',
    },
    success: {
      main: neonColors.green, // Profit Green
      light: '#33ffaa',
      dark: '#00cc66',
    },
    warning: {
      main: neonColors.orange, // Warning Orange
      light: '#ffbb33',
      dark: '#cc7700',
    },
    info: {
      main: neonColors.blue, // Data Blue
      light: '#33aaff',
      dark: '#0077cc',
    },
    error: {
      main: neonColors.pink, // Risk Pink
      light: '#ff3388',
      dark: '#cc0050',
    },
    background: {
      default: '#020817', // Darker space background
      paper: 'rgba(10, 15, 30, 0.6)', // More transparent for glass effect
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(200, 255, 255, 0.8)', // Slight cyan tint
    },
  },
  typography: {
    fontFamily: '"Space Grotesk", "Orbitron", "Inter", sans-serif',
    h1: {
      fontFamily: '"Orbitron", "Space Grotesk", sans-serif',
      fontWeight: 900,
      letterSpacing: '0.15em',
    },
    h2: {
      fontFamily: '"Orbitron", "Space Grotesk", sans-serif',
      fontWeight: 800,
      letterSpacing: '0.1em',
    },
    h3: {
      fontFamily: '"Orbitron", "Space Grotesk", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.08em',
    },
    h4: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
    h5: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.03em',
    },
    h6: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '0.95rem',
      letterSpacing: '0.02em',
    },
    body2: {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
    },
    button: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(0, 255, 255, 0.03) 0%, rgba(255, 0, 255, 0.03) 100%)',
          backgroundColor: 'rgba(10, 15, 30, 0.6)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          boxShadow: `
            0 0 40px rgba(0, 255, 255, 0.1),
            inset 0 0 20px rgba(255, 0, 255, 0.05),
            0 0 80px rgba(0, 255, 255, 0.05)
          `,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.5), transparent)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          textShadow: '0 0 10px currentColor',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '0',
            height: '0',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.5)',
            transform: 'translate(-50%, -50%)',
            transition: 'width 0.6s, height 0.6s',
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `
              0 10px 40px rgba(0, 255, 255, 0.4),
              0 0 60px rgba(255, 0, 255, 0.2)
            `,
            '&::before': {
              width: '300px',
              height: '300px',
            },
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',
          color: '#ffffff', // White text for dark theme
          fontWeight: 700,
          boxShadow: `
            0 4px 15px rgba(0, 255, 255, 0.4),
            inset 0 1px 2px rgba(255, 255, 255, 0.3)
          `,
          '&:hover': {
            color: '#ffffff', // Keep white text on hover
            background: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',
          },
        },
        outlined: {
          borderImage: 'linear-gradient(135deg, #00ffff, #ff00ff) 1',
          '&:hover': {
            borderImage: 'linear-gradient(135deg, #ff00ff, #00ffff) 1',
            background: 'rgba(0, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backdropFilter: 'blur(10px) saturate(180%)',
          background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1), rgba(255, 0, 255, 0.1))',
          border: '1px solid rgba(0, 255, 255, 0.3)',
          boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 255, 255, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
          },
        },
      },
    },
  },
});

// Holographic glass effect styles
export const glassEffect = {
  background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.05) 0%, rgba(255, 0, 255, 0.05) 100%)',
  backdropFilter: 'blur(20px) saturate(200%)',
  border: '1px solid rgba(0, 255, 255, 0.2)',
  boxShadow: `
    0 8px 32px rgba(0, 255, 255, 0.15),
    inset 0 2px 4px rgba(255, 255, 255, 0.1),
    0 0 80px rgba(255, 0, 255, 0.1)
  `,
};

// Neon glow animation
export const neonGlow = {
  animation: 'neonPulse 2s ease-in-out infinite',
  '@keyframes neonPulse': {
    '0%, 100%': {
      textShadow: `
        0 0 10px currentColor,
        0 0 20px currentColor,
        0 0 30px currentColor
      `,
    },
    '50%': {
      textShadow: `
        0 0 20px currentColor,
        0 0 30px currentColor,
        0 0 40px currentColor
      `,
    },
  },
};

export default arbitreasureTheme;